
import { EVMSnapshot, ProjectMetrics, FinancialMetrics, ChangeOrder } from '../types.ts';

/**
 * Professional Earned Value Management (EVM) Core
 * Designed for <$20M Construction Projects.
 */
export const calculateEVMMetrics = (snapshot: EVMSnapshot, bac: number): ProjectMetrics => {
  const { plannedValue, earnedValue, actualCost } = snapshot;

  // Variances
  const sv = earnedValue - plannedValue; // Schedule Variance
  const cv = earnedValue - actualCost;   // Cost Variance
  
  // Performance Indices
  const spi = plannedValue > 0 ? earnedValue / plannedValue : 1;
  const cpi = actualCost > 0 ? earnedValue / actualCost : 1;

  return {
    ...snapshot,
    spi,
    cpi,
    sv,
    cv,
    bac
  };
};

/**
 * Transform EVM snapshots into dashboard-ready financial metrics.
 * Now includes Revised Contract Value logic and earnedValue for forensic analysis.
 */
export const calculateFinancialMetrics = (snapshot: EVMSnapshot, originalBac: number, changeOrders: ChangeOrder[] = []): FinancialMetrics => {
  const { actualCost, plannedValue, earnedValue } = snapshot;
  
  // Calculate revised contract amount (BAC)
  const approvedCoTotal = changeOrders
    .filter(co => co.status === 'Approved')
    .reduce((acc, co) => acc + co.amount, 0);
    
  const revisedContractValue = originalBac + approvedCoTotal;
  
  const variance = earnedValue - actualCost;
  const burnRate = revisedContractValue > 0 ? (actualCost / revisedContractValue) * 100 : 0;
  const remainingBudget = revisedContractValue - actualCost;
  
  return {
    projectId: snapshot.projectId,
    date: snapshot.date,
    actualSpend: actualCost,
    projectedSpend: plannedValue,
    variance,
    burnRate,
    remainingBudget,
    revisedContractValue,
    // Fix: Include earnedValue for AI forensic assessments
    earnedValue
  };
};

export const getHealthColor = (index: number) => {
  if (index < 0.85) return 'danger';
  if (index < 0.95) return 'warning';
  return 'success';
};

export const formatCurrency = (val: number) => {
  return new Intl.NumberFormat('en-US', { 
    style: 'currency', 
    currency: 'USD', 
    maximumFractionDigits: 0 
  }).format(val);
};

export const formatCompactCurrency = (val: number) => {
  return new Intl.NumberFormat('en-US', { 
    style: 'currency', 
    currency: 'USD', 
    notation: 'compact',
    maximumFractionDigits: 1 
  }).format(val);
};

export const formatIndex = (val: number) => val.toFixed(2);
